import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { I } from '../lib/icons';
import { Pill } from './ui/Pill';

/**
 * FilterCombobox — durchsuchbare Multi-Select-Combobox fürs Filter-Panel
 * (FilterBar). Look & Feel der Upload-Produkt-Combobox (FacetPicker), aber
 * generisch (Produkt + Tags) und mit Count-Pills:
 * - Gewählte Werte zuerst als entfernbare Chips.
 * - Ein Eingabefeld: Tippen filtert live (NFC-normalisiert, beidseitig — manche
 *   Produkt-/Tag-Namen sind in der DB NFD), Chevron/Fokus öffnet die volle Liste.
 * - Liste zeigt nur (noch) nicht gewählte Optionen, je mit optionaler Count-Pill.
 * - SELECT-ONLY: kein Frei-Erstellen (Vokabular ist kontrolliert).
 *
 * `options` ist die VOLLE Liste (auch gewählte) — daraus wird das Chip-Label per
 * id aufgelöst. `selected`/`onToggle` halten denselben URL-CSV-Vertrag wie die
 * Facetten-Toggles in der LibraryPage.
 */
type Option = { id: string; name: string };

type Props = {
  label: string;
  options: Option[];
  selected: string[];
  counts: Map<string, number>;
  showCounts: boolean;
  onToggle: (id: string) => void;
  placeholder?: string;
  /** Text in der offenen Liste, wenn `options` leer ist (Loading/kein Vokabular). */
  emptyHint?: string;
};

const LABEL_CLASS =
  'block mb-1.5 text-xs2 font-semibold uppercase tracking-[0.06em] text-brand-green-600';
const INPUT_CLASS =
  'w-full h-9 pl-2.5 pr-9 rounded-md text-xs bg-white border border-ink-200 text-brand-green-800 placeholder:text-brand-green-400 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500';
const CHIP_CLASS =
  'inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-brand-green-50 border border-brand-green-200 text-xs2 text-brand-green-700';

export function FilterCombobox({
  label,
  options,
  selected,
  counts,
  showCounts,
  onToggle,
  placeholder = 'Suchen oder wählen…',
  emptyHint = 'Keine Optionen',
}: Props) {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Outside-Click schließt die Liste.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // id → name für Chip-Labels (aus der vollen Optionsliste; gewählte bleiben drin).
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.id, o.name);
    return m;
  }, [options]);

  // Treffer: nicht-gewählte Optionen, NFC-normalisierter Substring-Match auf name.
  const matches = useMemo(() => {
    const q = query.trim().normalize('NFC').toLowerCase();
    const sel = new Set(selected);
    return options.filter(
      (o) => !sel.has(o.id) && (q === '' || o.name.normalize('NFC').toLowerCase().includes(q)),
    );
  }, [options, selected, query]);
  const firstMatchId = matches[0]?.id;

  function pick(id: string): void {
    onToggle(id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={boxRef} className="relative" role="group" aria-label={`${label}-Filter`}>
      <span className={LABEL_CLASS}>{label}</span>

      {/* Gewählte Werte als entfernbare Chips (vor dem Eingabefeld). */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map((id) => (
            <span key={id} className={CHIP_CLASS}>
              <span className="truncate max-w-[10rem]">{nameById.get(id) ?? id}</span>
              <button
                type="button"
                onClick={() => onToggle(id)}
                aria-label={`${nameById.get(id) ?? id} entfernen`}
                className="text-brand-green-600 hover:text-brand-green-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (firstMatchId) pick(firstMatchId);
            } else if (e.key === 'Escape') {
              // Offene Liste/Text verbraucht das Esc — sonst schlösse dasselbe
              // Esc auch das umgebende Chip-Popover (ui/Popover, document-Listener).
              if (open || query) e.stopPropagation();
              setQuery('');
              setOpen(false);
            } else if (e.key === 'ArrowDown' && !open) {
              setOpen(true);
            }
          }}
          placeholder={placeholder}
          className={INPUT_CLASS}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={`${label} suchen oder wählen`}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? `${label}-Liste schließen` : `Alle ${label} anzeigen`}
          onClick={() => setOpen((o) => !o)}
          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-brand-green-500 hover:text-brand-green-700"
        >
          <I.chevD size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-20 mt-1 w-full bg-white border border-ink-200 rounded-md shadow-lg max-h-60 overflow-auto p-1"
          >
            {options.length === 0 ? (
              <div className="px-2 py-1.5 text-xs2 text-brand-green-600">{emptyHint}</div>
            ) : matches.length === 0 ? (
              <div className="px-2 py-1.5 text-xs2 text-brand-green-600">
                {query.trim() ? 'Nichts gefunden' : 'Alle ausgewählt'}
              </div>
            ) : (
              matches.map((o) => {
                const count = counts.get(o.id) ?? 0;
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => pick(o.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left text-brand-green-800 hover:bg-brand-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
                  >
                    <span className="flex-1 truncate">{o.name}</span>
                    {showCounts && count > 0 && (
                      <Pill tone="neutral" aria-label={`${count} Asset(s)`}>
                        {count}
                      </Pill>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
